import ExpandRightIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandRight.js';
import LinkIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Link.js';
import { LensTheme, StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import { ChevronRight } from '@mui/icons-material';
import {
  Avatar,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container, useTheme } from '@mui/system';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import { Badge } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import emptyStateIllustration from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast/Toast';
import { caseLinksUpdated } from 'src/components/pages/Case/Case.slice';
import { hiddenDots } from 'src/components/pages/Case/helpers/hiddenDots';
import {
  GetCaseLinksByCaseIdQuery,
  ReportStatus,
  useUnlinkCasesMutation,
} from 'src/graphql';
import { TRootState } from 'src/store';
import theme from 'src/theme/theme';
import { getReadableIdWithDash } from 'src/types';
import { capitaliseFirstLetter } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { DrawerShell } from '../../molecules/Drawer/DrawerShell';
import { ConfirmationBeforeRemoveModal } from '../ConfirmationBeforeRemoveModal/confirmationBeforeRemoveModal';
import { SpeakUpStatusColorMap } from '../helpers/SpeakUpColorMap';

type CaseLink = NonNullable<
  GetCaseLinksByCaseIdQuery['getCaseLinksByCaseId']
>[0];

const linkedCasesSelector = (state: TRootState) => {
  const { caseById, caseLinks } = state.case;
  return {
    caseId: caseById?.id ?? '',
    caseLinks: caseLinks ?? [],
  };
};

type OverviewRow = {
  key: string;
  type: 'text' | 'author' | 'chip' | 'status' | 'link';
  testId: string;
  value: string;
};

type LinkCase = CaseLink & {
  readableId: string;
  canView: boolean;
  canUnlink: boolean;
  status: string;
  linkDate: string;
  createdBy: string;
  linkingType: string;
};

const statusToBadgeVariant = (status: string): BadgeVariant => {
  const lower = status.toLowerCase();
  if (lower === ReportStatus.Closed.toLowerCase()) return 'closed';
  if (lower === ReportStatus.Investigating.toLowerCase())
    return 'investigating';
  if (lower === ReportStatus.New.toLowerCase()) return 'new';
  if (lower === ReportStatus.Read.toLowerCase()) return 'read';
  if (lower === ReportStatus.Draft.toLowerCase()) return 'draft';
  return 'investigating';
};

export const LinkCasesSection = () => {
  const { tokens, presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;

  const dispatch = useDispatch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<LinkCase | null>(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const { caseId, caseLinks } = useSelector(linkedCasesSelector);

  const { mutate: unlinkCases, isLoading: isUnlinking } =
    useUnlinkCasesMutation({
      onSuccess: () => {
        dispatch(
          caseLinksUpdated(caseLinks.filter((l) => l.id !== selectedLink?.id)),
        );
        setSelectedLink(null);
        handleSuccessToast({
          toastMessage: 'Case unlinking was successful',
          toastDescription: 'Case unlinked successfully.',
        });
      },
      onError: () => {
        setSelectedLink(null);
        handleErrorToast({
          toastMessage: 'Case unlinking failed',
          toastDescription: 'Unable to unlink the case. Please try again.',
        });
      },
    });

  const getLinkedCase = (link: CaseLink) =>
    link.SourceCase?.id === caseId ? link.TargetCase : link.SourceCase;

  const formatLinkList = caseLinks.map((link) => {
    const linkedCase = getLinkedCase(link);
    const canView =
      linkedCase?.AssociatedPermissions?.canViewCaseDetails ?? false;
    const canUnlink =
      linkedCase?.AssociatedPermissions?.canEditCaseDetails ?? false;
    const readableId = canView
      ? linkedCase?.id
        ? getReadableIdWithDash(linkedCase.id.toUpperCase())
        : '—'
      : hiddenDots;
    const status = linkedCase?.status ?? '';
    const linkDate = formatDate(link.createdAt, DateFormats.MM_DD_YYYY);
    const createdBy = canView ? link.CreatedByUser?.name : null;
    const linkType = capitaliseFirstLetter(link.linkType.replaceAll('_', ' '));
    return {
      ...link,
      linkedCase,
      canView,
      canUnlink,
      readableId,
      status,
      linkDate,
      createdBy,
      linkType,
    };
  });

  const rows: OverviewRow[] = [
    {
      key: 'Case ID',
      type: 'link',
      testId: 'case-id-title',
      value: selectedLink?.readableId || 'N/A',
    },
    {
      key: 'Status',
      type: 'status',
      testId: 'status-title',
      value: selectedLink?.status || 'N/A',
    },
    {
      key: 'Linking date',
      type: 'text',
      testId: 'linking-date-title',
      value: selectedLink?.linkDate || 'N/A',
    },
    {
      key: 'Linked by',
      type: 'author',
      testId: 'linked-by-title',
      value: selectedLink?.createdBy || 'N/A',
    },
    {
      key: 'Linking type',
      type: 'chip',
      testId: 'linking-type-title',
      value: selectedLink?.linkType || 'N/A',
    },
  ];

  const renderValue = (row: OverviewRow) => {
    switch (row.type) {
      case 'author':
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            {row.value && row.value !== 'N/A' ? (
              <>
                <Avatar
                  {...getAvatarProps({ size: 'medium', color: 'red' })}
                  aria-label={row.value}
                  role="img"
                >
                  {getInitials(row.value)}
                </Avatar>
                {row.value}
              </>
            ) : (
              'N/A'
            )}
          </Box>
        );
      case 'status':
        return (
          <StatusIndicator
            customColor={SpeakUpStatusColorMap(row.value)}
            label={capitaliseFirstLetter(row.value)}
            aria-label={`Case status - ${row.value}`}
          />
        );
      case 'chip':
        return <Chip label={row.value} />;
      case 'link':
        return (
          <Typography
            variant="body1"
            color={theme.atlas.semantic.color.type.default}
            sx={{
              cursor: selectedLink?.canView ? 'pointer' : 'default',
              textDecoration: 'underline',
            }}
            onClick={() => {
              if (selectedLink?.canView && selectedLink.id) {
                window.open(`/case?id=${selectedLink.id}`, '_blank');
              }
            }}
          >
            {row.value}
          </Typography>
        );
      default:
        return row.value;
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          padding: tokens.core.spacing[2].value,
          borderRadius: tokens.semantic.radius.lg.value,
          gap: tokens.core.spacing[3].value,
          backgroundColor: theme.atlas.semantic.color.background.base,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: tokens.core.spacing[3].value,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.atlas.semantic.color.status.notification.default,
              borderRadius: tokens.semantic.radius.md.value,
              display: 'flex',
              width: '48px',
              height: '48px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LinkIcon size="lg" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography variant="body1" fontWeight={600}>
              Link Cases
            </Typography>
            <Typography
              variant="body1"
              color={theme.atlas.semantic.color.type.default}
            >
              Review cases associated with this current case
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          onClick={() => setIsDrawerOpen(true)}
          endIcon={<ExpandRightIcon />}
        >
          See more
        </Button>
      </Box>

      <DrawerShell
        open={isDrawerOpen}
        title={!selectedLink ? 'Linked cases' : 'Linked case details'}
        onRequestClose={
          !selectedLink
            ? () => setIsDrawerOpen(false)
            : () => setSelectedLink(null)
        }
        onNext={
          !selectedLink
            ? () => setIsDrawerOpen(false)
            : () => setConfirmationDialogOpen(true)
        }
        nextTitle={!selectedLink ? 'Close' : 'Unlink case'}
        testId="linked-cases-drawer"
        nextDisabled={
          !selectedLink ? false : isUnlinking || !selectedLink.canUnlink
        }
        nextButtonState={!selectedLink ? 'default' : 'danger'}
      >
        {!selectedLink && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.core.spacing[2].value,
            }}
          >
            {formatLinkList.map((link) => {
              return (
                <Box
                  key={link.id}
                  onClick={() => setSelectedLink(link as unknown as LinkCase)}
                  aria-label="View linked case details"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: tokens.core.spacing[1].value,
                    padding: tokens.core.spacing[3].value,
                    border: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
                    borderRadius: tokens.semantic.radius.lg.value,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor:
                        theme.atlas.semantic.color.surface.variant,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Case {link.readableId}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.core.spacing[1].value,
                      }}
                    >
                      {link.canView && link.status && (
                        <Badge
                          variant={statusToBadgeVariant(link.status)}
                          text={capitaliseFirstLetter(
                            link.status.toLowerCase(),
                          )}
                          capitalize
                        />
                      )}
                      <IconButton size="small">
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color={theme.atlas.semantic.color.type.default}
                  >
                    Linking date:{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                    >
                      {link.linkDate}
                    </Typography>
                    {'  •  '}
                    Linking type:{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                    >
                      {link.linkType}
                    </Typography>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        {selectedLink && (
          <Box
            sx={{
              backgroundColor: theme.atlas.semantic.color.surface.variant,
              borderRadius: tokens.semantic.radius.md.value,
              padding: 3,
              display: 'flex',
              gap: '24px',
              flexDirection: 'column',
            }}
          >
            <Container
              sx={{
                backgroundColor: theme.atlas.semantic.color.surface.default,
                borderRadius: tokens.semantic.radius.md.value,
                padding: tokens.core.spacing[2].value,
              }}
            >
              <TableContainer>
                <Table>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.key}
                        </TableCell>
                        <TableCell
                          data-testid={row.testId}
                          sx={{ '&:focus-within': { boxShadow: 'none' } }}
                        >
                          {renderValue(row)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
            {selectedLink.notes && (
              <Container
                sx={{
                  backgroundColor: theme.atlas.semantic.color.surface.default,
                  borderRadius: tokens.semantic.radius.md.value,
                  padding: tokens.core.spacing[2].value,
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  Notes
                </Typography>
                <Typography
                  variant="body2"
                  color={theme.atlas.semantic.color.type.default}
                >
                  {selectedLink.notes}
                </Typography>
              </Container>
            )}
          </Box>
        )}
        {formatLinkList.length === 0 && !selectedLink && (
          <EmptyState
            title={'No linked cases'}
            description={'You have no linked cases for this case yet.'}
            illustration={
              <img
                src={emptyStateIllustration}
                alt="No linked cases"
                width="160"
                height="132"
              />
            }
          />
        )}
      </DrawerShell>
      <ConfirmationBeforeRemoveModal
        title="Confirm unlinking case"
        content={`Are you sure you want to unlink ${selectedLink?.readableId} from this case? This action will remove the connection between the cases but it won’t delete any data`}
        open={confirmationDialogOpen}
        onConfirm={() => {
          if (selectedLink) {
            unlinkCases({ linkId: selectedLink.id });
          }
          setConfirmationDialogOpen(false);
        }}
        onCancel={() => setConfirmationDialogOpen(false)}
        onClose={() => setConfirmationDialogOpen(false)}
        confirmButtonText="Unlink case"
        cancelButtonText="Stay here"
      />
    </>
  );
};
